'use client';

/**
 * SDOH Context Panel Component
 *
 * Displays comprehensive SDOH context for a service referral including:
 * - ML-predicted SDOH risk factors from workflow orchestration
 * - Area-level SDOH data (transportation, housing, climate)
 * - Manual SDOH assessment data
 * - Workflow context and trigger reasons
 *
 * This integrates pre-screening data that informs the consent outreach
 * and service matching workflow.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Brain,
  MapPin,
  Home,
  Car,
  Cloud,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Activity,
  Loader2,
  Workflow,
  Cpu,
  Building2,
  Thermometer,
  Droplets,
  Wind,
  Users,
  Heart,
  GraduationCap,
  Shield,
  Utensils,
  Info,
} from 'lucide-react';
import { serviceReferralsAPI, ReferralSDOHContext } from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SDOHContextPanelProps {
  referralId: number;
  className?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getRiskLevelColor(level: string | null | undefined): string {
  switch (level?.toLowerCase()) {
    case 'low':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'moderate':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'high':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'critical':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

function getScoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'text-gray-400';
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-yellow-600';
  if (score >= 20) return 'text-orange-600';
  return 'text-red-600';
}

function getRiskScoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'text-gray-400';
  if (score <= 20) return 'text-green-600';
  if (score <= 40) return 'text-blue-600';
  if (score <= 60) return 'text-yellow-600';
  if (score <= 80) return 'text-orange-600';
  return 'text-red-600';
}

function getProbabilityLabel(prob: number | null | undefined): string {
  if (prob === null || prob === undefined) return 'Unknown';
  if (prob >= 0.7) return 'High';
  if (prob >= 0.4) return 'Moderate';
  return 'Low';
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatPercent(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)}%`;
}

function getDomainIcon(domain: string): React.ReactNode {
  const iconClass = 'h-4 w-4';
  switch (domain.toUpperCase()) {
    case 'HOUSING':
      return <Home className={iconClass} />;
    case 'FOOD_NUTRITION':
      return <Utensils className={iconClass} />;
    case 'TRANSPORTATION':
      return <Car className={iconClass} />;
    case 'ECONOMIC_STABILITY':
      return <DollarSign className={iconClass} />;
    case 'EDUCATION_EMPLOYMENT':
      return <GraduationCap className={iconClass} />;
    case 'SOCIAL_SUPPORT':
      return <Users className={iconClass} />;
    case 'HEALTH_BEHAVIORS':
      return <Heart className={iconClass} />;
    case 'SAFETY':
      return <Shield className={iconClass} />;
    default:
      return <Activity className={iconClass} />;
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SDOHContextPanel({ referralId, className = '' }: SDOHContextPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [context, setContext] = useState<ReferralSDOHContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getReferralSDOHContext(referralId);
      setContext(data);
    } catch (err) {
      console.error('Failed to load SDOH context:', err);
      setError(t('serviceReferrals.sdohContext.loadError', 'Failed to load SDOH context data'));
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t('serviceReferrals.sdohContext.loading', 'Loading SDOH context...')}</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!context) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center text-gray-500">
          <Brain className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p>{t('serviceReferrals.sdohContext.noData', 'No SDOH context available')}</p>
        </CardContent>
      </Card>
    );
  }

  const hasMLPredictions = context.ml_predictions !== null;
  const hasAreaData = context.area_data !== null;
  const hasWorkflowContext = context.workflow_context !== null;
  const hasAssessment = context.assessment !== null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overall Risk Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Brain className="h-5 w-5" />
                {t('serviceReferrals.sdohContext.riskAnalysis', 'SDOH Risk Analysis')}
              </CardTitle>
              <CardDescription>
                {t('serviceReferrals.sdohContext.riskAnalysisDesc', 'Pre-screening analysis from ML models and workflow orchestration')}
              </CardDescription>
            </div>
            {context.overall_risk_level && (
              <Badge className={`${getRiskLevelColor(context.overall_risk_level)} border text-sm`}>
                {context.overall_risk_level.charAt(0).toUpperCase() + context.overall_risk_level.slice(1)} {t('serviceReferrals.sdohContext.risk', 'Risk')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Cpu className="h-5 w-5 mx-auto mb-1 text-purple-500" />
              <div className="text-xs text-gray-500">{t('serviceReferrals.sdohContext.mlAnalysis', 'ML Analysis')}</div>
              <div className="font-semibold">
                {hasMLPredictions ? t('serviceReferrals.sdohContext.available', 'Available') : t('serviceReferrals.sdohContext.notAvailable', 'Not Available')}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <MapPin className="h-5 w-5 mx-auto mb-1 text-blue-500" />
              <div className="text-xs text-gray-500">{t('serviceReferrals.sdohContext.areaData', 'Area Data')}</div>
              <div className="font-semibold">
                {hasAreaData ? context.area_data?.data_sources?.length + ' ' + t('serviceReferrals.sdohContext.sources', 'Sources') : t('serviceReferrals.sdohContext.notAvailable', 'Not Available')}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Workflow className="h-5 w-5 mx-auto mb-1 text-green-500" />
              <div className="text-xs text-gray-500">{t('serviceReferrals.sdohContext.workflow', 'Workflow')}</div>
              <div className="font-semibold">
                {hasWorkflowContext ? context.workflow_context?.workflow_status : t('serviceReferrals.sdohContext.none', 'None')}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Activity className="h-5 w-5 mx-auto mb-1 text-orange-500" />
              <div className="text-xs text-gray-500">{t('serviceReferrals.sdohContext.assessment', 'Assessment')}</div>
              <div className="font-semibold">
                {hasAssessment ? t('serviceReferrals.sdohContext.completed', 'Completed') : t('serviceReferrals.sdohContext.pending', 'Pending')}
              </div>
            </div>
          </div>

          {/* Priority Domains */}
          {context.priority_domains && context.priority_domains.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm font-medium text-gray-700 mb-2">{t('serviceReferrals.sdohContext.priorityDomains', 'Priority SDOH Domains')}</div>
              <div className="flex flex-wrap gap-2">
                {context.priority_domains.map((domain) => (
                  <Badge key={domain} variant="outline" className="flex items-center gap-1">
                    {getDomainIcon(domain)}
                    {domain.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Service Type Context */}
          {context.service_type_name && (
            <Alert className="mt-4 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>{t('serviceReferrals.sdohContext.serviceType', 'Service Type')}:</strong> {context.service_type_name}
                {context.member_zip_code && (
                  <span className="ml-2">| <strong>{t('serviceReferrals.sdohContext.zip', 'ZIP')}:</strong> {context.member_zip_code}</span>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Detailed Tabs */}
      <Tabs defaultValue={hasMLPredictions ? 'ml' : hasAreaData ? 'area' : 'assessment'}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="ml" disabled={!hasMLPredictions}>
            <Cpu className="h-4 w-4 mr-1" />
            {t('serviceReferrals.sdohContext.tabs.mlPredictions', 'ML Predictions')}
          </TabsTrigger>
          <TabsTrigger value="area" disabled={!hasAreaData}>
            <MapPin className="h-4 w-4 mr-1" />
            {t('serviceReferrals.sdohContext.tabs.areaData', 'Area Data')}
          </TabsTrigger>
          <TabsTrigger value="workflow" disabled={!hasWorkflowContext}>
            <Workflow className="h-4 w-4 mr-1" />
            {t('serviceReferrals.sdohContext.tabs.workflow', 'Workflow')}
          </TabsTrigger>
          <TabsTrigger value="assessment">
            <Activity className="h-4 w-4 mr-1" />
            {t('serviceReferrals.sdohContext.tabs.assessment', 'Assessment')}
          </TabsTrigger>
        </TabsList>

        {/* ML Predictions Tab */}
        <TabsContent value="ml">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Cpu className="h-5 w-5 text-purple-500" />
                {t('serviceReferrals.sdohContext.mlPredicted.title', 'ML-Predicted SDOH Risk Factors')}
              </CardTitle>
              <CardDescription>
                {t('serviceReferrals.sdohContext.mlPredicted.desc', 'Predictions based on claims analysis, engagement patterns, and historical data')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasMLPredictions && context.ml_predictions ? (
                <div className="space-y-4">
                  {/* Transportation Barrier */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Car className="h-5 w-5 text-blue-500" />
                        <span className="font-medium">{t('serviceReferrals.sdohContext.barriers.transportation', 'Transportation Barrier')}</span>
                      </div>
                      <Badge className={getRiskLevelColor(context.ml_predictions.transportation_barrier_confidence)}>
                        {getProbabilityLabel(context.ml_predictions.transportation_barrier_probability)} Risk
                      </Badge>
                    </div>
                    {context.ml_predictions.transportation_barrier_probability !== null && (
                      <div className="mt-2">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{t('serviceReferrals.sdohContext.probability', 'Probability')}</span>
                          <span>{(context.ml_predictions.transportation_barrier_probability * 100).toFixed(0)}%</span>
                        </div>
                        <Progress value={context.ml_predictions.transportation_barrier_probability * 100} />
                      </div>
                    )}
                  </div>

                  {/* Financial Barrier */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-500" />
                        <span className="font-medium">{t('serviceReferrals.sdohContext.barriers.financial', 'Financial Barrier')}</span>
                      </div>
                      <Badge className={getRiskLevelColor(context.ml_predictions.financial_barrier_confidence)}>
                        {getProbabilityLabel(context.ml_predictions.financial_barrier_probability)} Risk
                      </Badge>
                    </div>
                    {context.ml_predictions.financial_barrier_probability !== null && (
                      <div className="mt-2">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{t('serviceReferrals.sdohContext.probability', 'Probability')}</span>
                          <span>{(context.ml_predictions.financial_barrier_probability * 100).toFixed(0)}%</span>
                        </div>
                        <Progress value={context.ml_predictions.financial_barrier_probability * 100} />
                      </div>
                    )}
                  </div>

                  {/* Housing Instability */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Home className="h-5 w-5 text-orange-500" />
                        <span className="font-medium">{t('serviceReferrals.sdohContext.barriers.housing', 'Housing Instability')}</span>
                      </div>
                      <Badge className={getRiskLevelColor(context.ml_predictions.housing_instability_confidence)}>
                        {getProbabilityLabel(context.ml_predictions.housing_instability_probability)} Risk
                      </Badge>
                    </div>
                    {context.ml_predictions.housing_instability_probability !== null && (
                      <div className="mt-2">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{t('serviceReferrals.sdohContext.probability', 'Probability')}</span>
                          <span>{(context.ml_predictions.housing_instability_probability * 100).toFixed(0)}%</span>
                        </div>
                        <Progress value={context.ml_predictions.housing_instability_probability * 100} />
                      </div>
                    )}
                  </div>

                  {context.ml_predictions.predicted_at && (
                    <div className="text-xs text-gray-500 text-right">
                      {t('serviceReferrals.sdohContext.lastUpdated', 'Last updated')}: {fmt.dateTime(context.ml_predictions.predicted_at)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Cpu className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('serviceReferrals.sdohContext.noMlPredictions', 'ML predictions not available for this member')}</p>
                  <p className="text-sm mt-1">{t('serviceReferrals.sdohContext.predictionsGenerated', 'Predictions are generated from claims and engagement analysis')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Area Data Tab */}
        <TabsContent value="area">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-5 w-5 text-blue-500" />
                {t('serviceReferrals.sdohContext.areaDataTitle', 'Area-Level SDOH Data')}
              </CardTitle>
              <CardDescription>
                {t('serviceReferrals.sdohContext.areaDataDesc', 'Geographic risk factors for ZIP code')} {context.member_zip_code || 'N/A'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasAreaData && context.area_data ? (
                <div className="space-y-6">
                  {/* Transportation */}
                  {context.area_data.transportation && (
                    <div>
                      <h4 className="flex items-center gap-2 font-medium mb-3">
                        <Car className="h-4 w-4 text-blue-500" />
                        {t('serviceReferrals.sdohContext.transportationAccess', 'Transportation Access')}
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {context.area_data.transportation.walk_score ?? '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.walkScore', 'Walk Score')}</div>
                          {context.area_data.transportation.walk_score_description && (
                            <div className="text-xs text-gray-500 mt-1">
                              {context.area_data.transportation.walk_score_description}
                            </div>
                          )}
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {context.area_data.transportation.transit_score ?? '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.transitScore', 'Transit Score')}</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {context.area_data.transportation.bike_score ?? '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.bikeScore', 'Bike Score')}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.noVehicle', 'No Vehicle Households')}:</span>
                          <span className="font-medium">
                            {formatPercent(context.area_data.transportation.pct_no_vehicle_households)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.avgCommute', 'Avg Commute Time')}:</span>
                          <span className="font-medium">
                            {context.area_data.transportation.mean_commute_time_minutes?.toFixed(0) ?? '-'} min
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Housing */}
                  {context.area_data.housing && (
                    <div>
                      <h4 className="flex items-center gap-2 font-medium mb-3">
                        <Home className="h-4 w-4 text-orange-500" />
                        {t('serviceReferrals.sdohContext.housingRisk', 'Housing Risk Factors')}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className={`text-xl font-bold ${getRiskScoreColor(context.area_data.housing.eviction_rate)}`}>
                            {formatPercent(context.area_data.housing.eviction_rate)}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.evictionRate', 'Eviction Rate')}</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className={`text-xl font-bold ${getRiskScoreColor(context.area_data.housing.pct_cost_burdened)}`}>
                            {formatPercent(context.area_data.housing.pct_cost_burdened)}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.costBurdened', 'Cost Burdened')}</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className="text-xl font-bold text-orange-600">
                            {formatCurrency(context.area_data.housing.median_gross_rent)}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.medianRent', 'Median Rent')}</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className={`text-xl font-bold ${getRiskScoreColor(context.area_data.housing.housing_risk_score)}`}>
                            {context.area_data.housing.housing_risk_score?.toFixed(0) ?? '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.riskScore', 'Risk Score')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Climate */}
                  {context.area_data.climate && (
                    <div>
                      <h4 className="flex items-center gap-2 font-medium mb-3">
                        <Cloud className="h-4 w-4 text-cyan-500" />
                        {t('serviceReferrals.sdohContext.environmentalRisks', 'Environmental Risks')}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 bg-cyan-50 rounded-lg text-center">
                          <Thermometer className="h-5 w-5 mx-auto mb-1 text-cyan-600" />
                          <div className="text-lg font-bold text-cyan-600">
                            {context.area_data.climate.aqi_annual_median?.toFixed(0) ?? '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.aqiMedian', 'AQI Median')}</div>
                        </div>
                        <div className="p-3 bg-cyan-50 rounded-lg text-center">
                          <Droplets className="h-5 w-5 mx-auto mb-1 text-cyan-600" />
                          <div className="text-lg font-bold text-cyan-600">
                            {context.area_data.climate.flood_risk_level || '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.floodRisk', 'Flood Risk')}</div>
                        </div>
                        <div className="p-3 bg-cyan-50 rounded-lg text-center">
                          <Wind className="h-5 w-5 mx-auto mb-1 text-cyan-600" />
                          <div className="text-lg font-bold text-cyan-600">
                            {context.area_data.climate.wildfire_risk_level || '-'}
                          </div>
                          <div className="text-xs text-gray-600">{t('serviceReferrals.sdohContext.wildfireRisk', 'Wildfire Risk')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Area SDOH */}
                  {context.area_data.area_sdoh && (
                    <div>
                      <h4 className="flex items-center gap-2 font-medium mb-3">
                        <Building2 className="h-4 w-4 text-purple-500" />
                        {t('serviceReferrals.sdohContext.socioeconomic', 'Socioeconomic Indicators')}
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.medianIncome', 'Median Income')}:</span>
                          <span className="font-medium">
                            {formatCurrency(context.area_data.area_sdoh.median_household_income)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.povertyRate', 'Poverty Rate')}:</span>
                          <span className={`font-medium ${getRiskScoreColor(context.area_data.area_sdoh.poverty_rate)}`}>
                            {formatPercent(context.area_data.area_sdoh.poverty_rate)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.unemployment', 'Unemployment')}:</span>
                          <span className={`font-medium ${getRiskScoreColor(context.area_data.area_sdoh.unemployment_rate)}`}>
                            {formatPercent(context.area_data.area_sdoh.unemployment_rate)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.uninsured', 'Uninsured')}:</span>
                          <span className={`font-medium ${getRiskScoreColor(context.area_data.area_sdoh.uninsured_rate)}`}>
                            {formatPercent(context.area_data.area_sdoh.uninsured_rate)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.snapParticipation', 'SNAP Participation')}:</span>
                          <span className="font-medium">
                            {formatPercent(context.area_data.area_sdoh.snap_participation_rate)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span className="text-gray-600">{t('serviceReferrals.sdohContext.foodDesert', 'Food Desert')}:</span>
                          <span className={`font-medium ${context.area_data.area_sdoh.food_desert_flag ? 'text-red-600' : 'text-green-600'}`}>
                            {context.area_data.area_sdoh.food_desert_flag ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('serviceReferrals.sdohContext.noAreaData', 'Area-level SDOH data not available')}</p>
                  <p className="text-sm mt-1">{t('serviceReferrals.sdohContext.dataBasedOnZip', 'Data is based on member ZIP code')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Tab */}
        <TabsContent value="workflow">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Workflow className="h-5 w-5 text-green-500" />
                {t('serviceReferrals.sdohContext.workflowContext', 'Workflow Context')}
              </CardTitle>
              <CardDescription>
                {t('serviceReferrals.sdohContext.workflowContextDesc', 'How this referral was triggered and initiated')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasWorkflowContext && context.workflow_context ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">{t('serviceReferrals.sdohContext.workflowStatus', 'Workflow Status')}</div>
                      <Badge className={getRiskLevelColor(
                        context.workflow_context.workflow_status === 'completed' ? 'low' :
                        context.workflow_context.workflow_status === 'in_progress' ? 'moderate' : 'high'
                      )}>
                        {context.workflow_context.workflow_status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">{t('serviceReferrals.sdohContext.initialRiskLevel', 'Initial Risk Level')}</div>
                      <Badge className={`${getRiskLevelColor(context.workflow_context.initial_risk_level)} border`}>
                        {context.workflow_context.initial_risk_level || t('serviceReferrals.sdohContext.notSet', 'Not Set')}
                      </Badge>
                    </div>
                  </div>

                  {context.workflow_context.initial_sdoh_score !== null && (
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-gray-500 mb-2">{t('serviceReferrals.sdohContext.initialSdohScore', 'Initial SDOH Score (Pre-screening)')}</div>
                      <div className="flex items-center gap-4">
                        <div className={`text-3xl font-bold ${getRiskScoreColor(context.workflow_context.initial_sdoh_score)}`}>
                          {context.workflow_context.initial_sdoh_score?.toFixed(1)}
                        </div>
                        <div className="flex-1">
                          <Progress value={context.workflow_context.initial_sdoh_score} className="h-3" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('serviceReferrals.sdohContext.scoreFromMl', 'Score from ML pre-screening at workflow initiation (0-100, higher = more risk)')}
                      </p>
                    </div>
                  )}

                  {context.workflow_context.trigger_reason && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="text-sm font-medium text-green-800 mb-1">{t('serviceReferrals.sdohContext.triggerReason', 'Trigger Reason')}</div>
                      <p className="text-green-700">{context.workflow_context.trigger_reason}</p>
                    </div>
                  )}

                  {context.workflow_context.template_name && (
                    <div className="text-sm text-gray-500">
                      <strong>{t('serviceReferrals.sdohContext.workflowTemplate', 'Workflow Template')}:</strong> {context.workflow_context.template_name}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Workflow className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('serviceReferrals.sdohContext.noWorkflow', 'No workflow context available')}</p>
                  <p className="text-sm mt-1">{t('serviceReferrals.sdohContext.notFromWorkflow', 'This referral was not created from a workflow')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessment Tab */}
        <TabsContent value="assessment">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-5 w-5 text-orange-500" />
                {t('serviceReferrals.sdohContext.manualAssessment', 'Manual SDOH Assessment')}
              </CardTitle>
              <CardDescription>
                {t('serviceReferrals.sdohContext.assessmentByCareTeam', 'Assessment completed by care team')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasAssessment && context.assessment ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-500">{t('serviceReferrals.sdohContext.assessmentType', 'Assessment Type')}</div>
                      <div className="font-medium capitalize">
                        {context.assessment.assessment_type?.replace(/_/g, ' ')}
                      </div>
                    </div>
                    <Badge className={`${getRiskLevelColor(context.assessment.risk_level)} border`}>
                      {context.assessment.risk_level || t('serviceReferrals.sdohContext.notSet', 'Not Set')} {t('serviceReferrals.sdohContext.risk', 'Risk')}
                    </Badge>
                  </div>

                  {context.assessment.overall_sdoh_score !== null && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">{t('serviceReferrals.sdohContext.overallSdohScore', 'Overall SDOH Score')}</span>
                        <span className={`text-xl font-bold ${getRiskScoreColor(context.assessment.overall_sdoh_score)}`}>
                          {context.assessment.overall_sdoh_score?.toFixed(1)}%
                        </span>
                      </div>
                      <Progress value={context.assessment.overall_sdoh_score} />
                    </div>
                  )}

                  {/* Identified Needs */}
                  {context.needs && context.needs.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">{t('serviceReferrals.sdohContext.identifiedNeeds', 'Identified Needs')}</div>
                      <div className="space-y-2">
                        {context.needs.map((need) => (
                          <div key={need.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                            {getDomainIcon(need.sdoh_domain_code)}
                            <span className="flex-1 text-sm">{need.need_description || need.sdoh_domain_code.replace(/_/g, ' ')}</span>
                            <Badge variant="outline" className="text-xs capitalize">{need.severity}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {context.assessment.assessed_at && (
                    <div className="text-xs text-gray-500 text-right">
                      {t('serviceReferrals.sdohContext.assessed', 'Assessed')}: {fmt.dateTime(context.assessment.assessed_at)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('serviceReferrals.sdohContext.noManualAssessment', 'No manual assessment completed')}</p>
                  <p className="text-sm mt-1">{t('serviceReferrals.sdohContext.useAssessmentForm', 'Use the SDOH Assessment form to complete an assessment')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default SDOHContextPanel;
